import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { formatNcmCode } from 'utils/formatters/ncmFormatter';

const NcmCustomize = ({
  searchResults,
  selectedRow,
  onBack,
  onContinue,
  formData,
  handleFormChange,
  ncmDescription
}) => {
  // If we have search results, use the selected NCM
  // Otherwise, we're in edit mode with existing NCM data
  const selectedNcm = searchResults?.tec?.[selectedRow];
  const displayCode = selectedNcm?.ncm?.codigo || formData.ncm_code;
  const displayDescription = selectedNcm?.ncm?.descricao || ncmDescription || 'NCM details not available';

  // Local state for raw input text — prevents decimal point loss during typing
  const toPercent = (decimal) => {
    if (decimal === '' || decimal === null || decimal === undefined) return '0';
    const pct = parseFloat((parseFloat(decimal) * 100).toFixed(4));
    return isNaN(pct) ? '0' : pct.toString();
  };

  const [rawReducao, setRawReducao] = React.useState(() => toPercent(formData.reducao_base_pis_cofins));
  const [rawMajoracao, setRawMajoracao] = React.useState(() => toPercent(formData.majoracao_cofins));

  // Sync local state when formData changes externally (e.g. modal reopen)
  React.useEffect(() => {
    setRawReducao(toPercent(formData.reducao_base_pis_cofins));
  }, [formData.reducao_base_pis_cofins]);

  React.useEffect(() => {
    setRawMajoracao(toPercent(formData.majoracao_cofins));
  }, [formData.majoracao_cofins]);

  // Handle input changes for percentage fields
  const handlePercentageChange = (e) => {
    const { name, value } = e.target;

    // Allow empty value
    if (value === '') {
      if (name === 'reducao_base_pis_cofins') setRawReducao('');
      if (name === 'majoracao_cofins') setRawMajoracao('');
      handleFormChange({ target: { name, value: 0 } });
      return;
    }

    // Normalize Brazilian decimals (0,8 -> 0.8) BEFORE stripping non-numerics.
    // Mirrors NcmSearch.jsx:34.
    let processedValue = value.replace(',', '.').replace(/[^\d.]/g, '');
    const parts = processedValue.split('.');
    if (parts.length > 2) {
      processedValue = `${parts[0]}.${parts.slice(1).join('')}`;
    }

    // Update local raw state (preserves "0.", "1.2", etc.)
    if (name === 'reducao_base_pis_cofins') setRawReducao(processedValue);
    if (name === 'majoracao_cofins') setRawMajoracao(processedValue);

    // Convert percentage input to decimal for storage (10 -> 0.10)
    const numericValue = parseFloat(processedValue);
    if (!isNaN(numericValue)) {
      const decimalValue = parseFloat((numericValue / 100).toFixed(6));
      handleFormChange({ target: { name, value: decimalValue } });
    }
  };

  if (!displayCode) return null;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 2 }}
      >
        Back to Search
      </Button>

      {/* NCM Summary Box - Compact version */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 1.5,
          bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : 'rgba(33, 150, 243, 0.04)',
          border: '1px solid',
          borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.5)' : 'rgba(33, 150, 243, 0.25)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <ReceiptIcon color="primary" sx={{ mr: 2, mt: 0.5 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight="medium">{formatNcmCode(displayCode)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {displayDescription}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Custom Tax Values Section */}
      <Paper
        elevation={0}
        sx={{
          p: 0,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            bgcolor: theme => theme.palette.mode === 'dark'
              ? 'rgba(0, 150, 136, 0.15)'
              : 'rgba(0, 150, 136, 0.06)',
            borderBottom: '1px solid',
            borderColor: theme => theme.palette.mode === 'dark'
              ? 'rgba(0, 150, 136, 0.3)'
              : 'rgba(0, 150, 136, 0.2)',
          }}
        >
          <SettingsIcon sx={{ color: 'success.main' }} />
          <Typography
            variant="subtitle1"
            fontWeight="medium"
            color="text.primary"
          >
            Customize Tax Values
          </Typography>
        </Box>

        <Box sx={{ p: 2.5, bgcolor: 'background.paper' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set custom tax values for this NCM code. These values will be used for tax calculations
            and regulatory compliance.
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={8} md={6}>
              <TextField
                fullWidth
                label="PIS/COFINS Reduction Base (%)"
                name="reducao_base_pis_cofins"
                value={rawReducao}
                onChange={handlePercentageChange}
                variant="outlined"
                size="small"
                type="text"
                InputProps={{
                  sx: { borderRadius: 1 },
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                inputProps={{ inputMode: 'decimal' }}
                helperText="Reduction percentage for PIS/COFINS calculation"
              />
            </Grid>
            <Grid item xs={12} sm={8} md={6}>
              <TextField
                fullWidth
                label="Majoração COFINS (%)"
                name="majoracao_cofins"
                value={rawMajoracao}
                onChange={handlePercentageChange}
                variant="outlined"
                size="small"
                type="text"
                InputProps={{
                  sx: { borderRadius: 1 },
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                inputProps={{ inputMode: 'decimal' }}
                helperText="Surcharge is cost only, does not generate credit."
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={onContinue}
              sx={{ borderRadius: 1.5 }}
            >
              Continue to Review
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default NcmCustomize;